import { useEffect, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";
import { getFirebaseErrorMessage } from "@/lib/firebaseErrors";
import type { RankingBundlePointer } from "@/types/rankingBundle";
import { pickLatestReadyBundle } from "@/utils/rankingBundles";

export interface UseTaskRankingBundleResult {
  bundle: RankingBundlePointer | null;
  loading: boolean;
  error: Error | null;
}

/**
 * Reads the latest `"ready"` AI-trait ranking-bundle pointer doc for a task at
 * `organizations/{orgId}/trials/{trialId}/experiments/{experimentId}/tasks/{taskId}/rankingBundles`.
 * Org comes from the selected organization; the ai-traits route always supplies a concrete
 * experimentId, so (unlike useTask) no experiment resolution is needed. Returns null when the task
 * has no ready bundle. Client-side latest-selection — no composite index required.
 */
export function useTaskRankingBundle(
  trialId: string | undefined,
  experimentId: string | undefined,
  taskId: string | undefined,
): UseTaskRankingBundleResult {
  const { selectedOrganization } = useAuth();
  const [bundle, setBundle] = useState<RankingBundlePointer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!trialId || !experimentId || !taskId || !selectedOrganization?.id) {
      setBundle(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    const bundlesRef = collection(
      db,
      `organizations/${selectedOrganization.id}/trials/${trialId}/experiments/${experimentId}/tasks/${taskId}/rankingBundles`,
    );

    const unsubscribe = onSnapshot(
      bundlesRef,
      (snapshot) => {
        const pointers = snapshot.docs.map(
          (doc) => ({ ...(doc.data() as RankingBundlePointer), bundleId: doc.id }),
        );
        setBundle(pickLatestReadyBundle(pointers));
        setLoading(false);
      },
      (err) => {
        console.error("Error listening to task ranking bundles:", err);
        setError(new Error(getFirebaseErrorMessage(err, "load AI traits")));
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [selectedOrganization?.id, trialId, experimentId, taskId]);

  return { bundle, loading, error };
}
